/**
 * Clip `text` to at most `maxChars` characters, breaking on a sentence or
 * paragraph boundary rather than mid-word. Used for trial previews where the
 * naive `text.slice(0, N)` produced cuts like "...ist die Anfo" that read as
 * a rendering bug.
 *
 * Looks for, in priority order, within the last ~40% of the slice:
 *   - paragraph break (\n\n)
 *   - line break (\n)
 *   - sentence-ending punctuation followed by whitespace (. / ! / ? + space)
 * Falls back to the last whitespace, and finally to a hard slice if none of
 * those exist in a sensible position.
 */
/**
 * Strip inline markdown markers (bold, italic, code, headings, links) and
 * collapse whitespace so the result can be rendered as plain text — e.g. in
 * the hub strip teaser or a Studio suggestion card. Block-level structure is
 * intentionally flattened.
 */
export const stripInlineMarkdown = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)]\(([^)]+)\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|\W)\*([^*\s][^*]*?)\*(?=\W|$)/g, '$1$2')
    .replace(/(^|\W)_([^_\s][^_]*?)_(?=\W|$)/g, '$1$2')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const clipAtSentenceBoundary = (text, maxChars = 550) => {
  if (!text) return '';
  if (text.length <= maxChars) return text;

  const slice = text.slice(0, maxChars);
  const minBoundary = Math.floor(maxChars * 0.6);

  const paraIdx = slice.lastIndexOf('\n\n');
  if (paraIdx >= minBoundary) return slice.slice(0, paraIdx).trim();

  const nlIdx = slice.lastIndexOf('\n');
  if (nlIdx >= minBoundary) return slice.slice(0, nlIdx).trim();

  const sentenceRe = /[.!?](?=\s|$)/g;
  let m;
  let lastSentence = -1;
  while ((m = sentenceRe.exec(slice)) !== null) {
    if (m.index + 1 >= minBoundary) lastSentence = m.index + 1;
  }
  if (lastSentence > 0) return slice.slice(0, lastSentence).trim();

  const spaceIdx = slice.lastIndexOf(' ');
  if (spaceIdx >= minBoundary) return slice.slice(0, spaceIdx).trim();

  return slice;
};

const isLowValueRequirementsOpening = (paragraph) => {
  const trimmed = paragraph.trim();
  const lower = trimmed.toLowerCase();

  return (
    /^#{1,6}\s*executive take\b/i.test(trimmed) ||
    /^yes[—–-]?\s*this is .*floor plan/i.test(trimmed) ||
    lower.includes('recognizable floor plan') ||
    /^based on the attached floor plan\b/i.test(trimmed) ||
    /^based on the provided floor plan\b/i.test(trimmed) ||
    (/^anhand des (angehängten|vorliegenden) grundriss/i.test(lower) && lower.includes('klar'))
  );
};

export const cleanUserRequirementsResponse = (text) => {
  if (!text) return '';

  const paragraphs = String(text)
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean);

  while (paragraphs.length > 1 && isLowValueRequirementsOpening(paragraphs[0])) {
    paragraphs.shift();
  }

  return paragraphs.join('\n\n').trim();
};

export const formatMarkdownLite = (text) => {
  if (!text) return '';

  return text
    // Markdown tables (GFM-style). Must run before other transforms so the
    // inline replacements below (bold, italic, code) apply to cell contents,
    // but before the paragraph splitter would otherwise wrap the lines.
    .replace(
      /^[ \t]*\|(.+)\|[ \t]*\n[ \t]*\|([-:|\s]+)\|[ \t]*\n((?:[ \t]*\|.*\|[ \t]*\n?)+)/gm,
      (match, headerLine, _sep, body) => {
        const headers = headerLine.split('|').map(h => h.trim());
        const rows = body.trim().split('\n').map(row =>
          row.replace(/^[ \t]*\|/, '').replace(/\|[ \t]*$/, '').split('|').map(c => c.trim())
        );
        const thead = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
        // Trailing `\n\n` so the paragraph splitter (and other line-anchored
        // regexes below) see a block boundary after the table. The row regex
        // optionally consumes the last row's newline, so without this the
        // next line (heading, prose, etc.) was glued onto `</table>` and
        // skipped further block-level transforms.
        return `<table class="markdown-table">${thead}${tbody}</table>\n\n`;
      }
    )

    // Headers - ordered longest to shortest to avoid conflicts.
    // Sized so each level reads visibly as a heading (text-subtitle-1 was
    // indistinguishable from a bold paragraph). Monotonically shrinks
    // from # to ####.
    .replace(/^#### (.*$)/gm, '<h6 class="text-subtitle-2 font-weight-bold mt-2 mb-1">$1</h6>')
    .replace(/^### (.*$)/gm, '<h5 class="text-subtitle-1 font-weight-bold mt-3 mb-1">$1</h5>')
    .replace(/^## (.*$)/gm, '<h4 class="text-h6 font-weight-bold mt-4 mb-2">$1</h4>')
    .replace(/^# (.*$)/gm, '<h3 class="text-h5 font-weight-bold mt-5 mb-2">$1</h3>')

    // Horizontal rule (must run before paragraph wrapping so the line
    // doesn't get re-wrapped as a paragraph and rendered as literal text).
    .replace(/^\s*([-*_])\1{2,}\s*$/gm, '<hr class="my-4" />')

    // Bold and Italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')

    // Lists
    .replace(/^\s*\d+\.\s+([^\n]*)/gm, '<li>$1</li>') // Ordered lists
    .replace(/^\s*[-*]\s+([^\n]*)/gm, '<li>$1</li>') // Unordered lists
    .replace(/(<li>.*<\/li>)\n+(?=<li>)/g, '$1') // Group consecutive list items onto one line
    // Wrap every contiguous <li>...</li> chain in a <ul>. The previous
    // version required the chain to be followed by a non-tag char, which
    // dropped the wrapper when the next line was another block element
    // (heading, hr, another list) — leaving orphan <li> outside any <ul>,
    // which the browser renders without padding so the bullets sit
    // flush-left and look misaligned vs. the properly-wrapped lists.
    .replace(/<li>.*<\/li>/g, '<ul>$&</ul>')

    // Code blocks and inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>')

    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')

    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')

    // Paragraphs
    .replace(/\n\s*\n/g, '</p><p>')
    .replace(/^([^<].*)/gm, (match) => {
      if (match.trim() && !match.startsWith('<')) {
        return `<p>${match}</p>`;
      }
      return match;
    })

    // Strip <p> wrappers around block-level elements. The naive paragraph
    // splitter above puts <p>...</p> around every double-newline gap, so
    // a blockquote / heading / list / hr ends up nested inside <p>. Browsers
    // auto-close the <p> on encountering a block element, then insert an
    // empty <p></p> to balance the trailing </p> — that's where the stray
    // gaps in the rendered output come from. Drop those wrappers explicitly
    // so the emitted HTML is valid and has no empty paragraphs.
    .replace(/<p>\s*(<(?:h[1-6]|hr|ul|ol|blockquote|pre|table)\b[^>]*>)/g, '$1')
    .replace(/(<\/(?:h[1-6]|ul|ol|blockquote|pre|table)>)\s*<\/p>/g, '$1')
    .replace(/(<hr\b[^>]*\/?>)\s*<\/p>/g, '$1')

    // Clean up empty paragraphs
    .replace(/<p>\s*<\/p>/g, '')

    // Fix any double-wrapped paragraphs
    .replace(/<p><p>/g, '<p>')
    .replace(/<\/p><\/p>/g, '</p>')

    // Line breaks (only for single newlines within paragraphs)
    .replace(/([^>\n])\n(?!<)([^<])/g, '$1<br>$2');
};
